// Client for the shared satoshiadvance.com backend (the `pricedisplay` repo).
// Mirrors the tanks integration: prod talks directly to the satoshi-homepage
// Heroku app; dev talks to a local backend on :5000. Override with
// REACT_APP_SATOSHI_BACKEND_URL if you run the backend somewhere else.

const resolveBackendUrl = () => {
  if (process.env.REACT_APP_SATOSHI_BACKEND_URL) {
    return process.env.REACT_APP_SATOSHI_BACKEND_URL.replace(/\/$/, '');
  }
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (host === 'localhost' || host === '127.0.0.1') {
    return 'http://localhost:5000';
  }
  return 'https://satoshi-homepage-59650ad0a3ce.herokuapp.com';
};

export const BACKEND_URL = resolveBackendUrl();

// Persist the session so the user stays logged in on return.
const TOKEN_KEY = 'bitwheel.auth.token';
const EMAIL_KEY = 'bitwheel.auth.email';

// Shared cross-app cookies on .satoshiadvance.com: a login on ANY of the
// games (or the homepage) drops the session token + email here, so the other
// apps can pick the session up without asking the player to log in again.
const CROSS_COOKIE_DOMAIN = '.satoshiadvance.com';
const CROSS_TOKEN_COOKIE = 'satoshiToken';
const CROSS_EMAIL_COOKIE = 'userEmail';

const readCookie = (name) => {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
};

const writeCrossCookie = (name, value) => {
  if (typeof document === 'undefined') {
    return;
  }
  // Only useful on the real domain; harmless elsewhere (browser ignores a
  // mismatched Domain attribute on localhost).
  document.cookie = `${name}=${encodeURIComponent(value)}; domain=${CROSS_COOKIE_DOMAIN}; path=/; max-age=86400; SameSite=Lax; Secure`;
};

const clearCrossCookie = (name) => {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${name}=; domain=${CROSS_COOKIE_DOMAIN}; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Lax; Secure`;
};

export const getStoredSession = () => {
  if (typeof window === 'undefined') {
    return { token: null, email: null };
  }
  // Local session first, then fall back to the cross-app cookie set by a
  // login on another Satoshi Advance app. The restored token is validated
  // against the backend before the user is treated as logged in.
  return {
    token: window.localStorage.getItem(TOKEN_KEY) || readCookie(CROSS_TOKEN_COOKIE),
    email: window.localStorage.getItem(EMAIL_KEY) || readCookie(CROSS_EMAIL_COOKIE)
  };
};

export const storeSession = ({ token, email }) => {
  if (typeof window === 'undefined') {
    return;
  }
  if (token) {
    window.localStorage.setItem(TOKEN_KEY, token);
    writeCrossCookie(CROSS_TOKEN_COOKIE, token);
  }
  if (email) {
    window.localStorage.setItem(EMAIL_KEY, email);
    writeCrossCookie(CROSS_EMAIL_COOKIE, email);
  }
};

export const clearSession = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(EMAIL_KEY);
  clearCrossCookie(CROSS_TOKEN_COOKIE);
  clearCrossCookie(CROSS_EMAIL_COOKIE);
};

// Clear only this app's local copy of the session — used when ANOTHER
// Satoshi Advance app already logged the user out (the shared cookies are
// gone); clearing cookies again could wipe a newer user's session.
export const clearLocalSession = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(EMAIL_KEY);
};

// Is any Satoshi Advance session active (on any app)? The shared userEmail
// cookie is the cross-app source of truth.
export const readCrossSessionEmail = () => readCookie(CROSS_EMAIL_COOKIE);

// Tell the backend to invalidate the account's cross-app tokens so sessions
// stored by other apps stop validating (best-effort, fire and forget).
export const logoutEverywhere = (email) => {
  if (!email || typeof fetch === 'undefined') {
    return;
  }
  fetch(`${BACKEND_URL}/api/users/logout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  }).catch(() => {
    // Cookies are cleared regardless; token expiry is the backstop.
  });
};

// Shared request helper: JSON in/out, attaches the bearer token, and surfaces
// the backend's error message on a non-2xx response.
const request = async (path, { method = 'GET', body, token } = {}) => {
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${BACKEND_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined
  });

  let data = null;
  try {
    data = await response.json();
  } catch (err) {
    data = null;
  }

  if (!response.ok) {
    const message = (data && data.error) || `Request failed (${response.status})`;
    const error = new Error(message);
    error.status = response.status;
    error.code = (data && data.code) || undefined;
    throw error;
  }

  return data;
};

export const register = (email, password) =>
  request('/api/users/bit-wheel/register', { method: 'POST', body: { email, password } });

export const login = (email, password) =>
  request('/api/users/bit-wheel/login', { method: 'POST', body: { email, password } });

export const requestPasswordReset = (email) =>
  request('/api/users/bit-wheel/request-password-reset', { method: 'POST', body: { email } });

export const resetPassword = (email, token, password) =>
  request('/api/users/bit-wheel/reset-password', { method: 'POST', body: { email, token, password } });

export const fetchAccount = (token) =>
  request('/api/users/bit-wheel/me', { method: 'GET', token });

export const purchaseSpins = (payload, token) =>
  request('/api/users/bit-wheel/purchase', { method: 'POST', body: payload, token });

export const recordSpin = (payload, token) =>
  request('/api/users/bit-wheel/record-spin', { method: 'POST', body: payload, token });

// --- Game engine (solo high-score mode) ---

export const startGame = (token) =>
  request('/api/users/bit-wheel/game/start', { method: 'POST', token });

export const gameSpin = (payload, token) =>
  request('/api/users/bit-wheel/game/spin', { method: 'POST', body: payload, token });

export const fetchGame = (sessionId, token) =>
  request(`/api/users/bit-wheel/game/${sessionId}`, { method: 'GET', token });

export const fetchLeaderboard = () =>
  request('/api/users/bit-wheel/leaderboard', { method: 'GET' });

// Same endpoint tanks uses — one gamertag across all Satoshi Advance games.
export const updateGamerTag = (email, gamerTag) =>
  request('/api/users/update-gamertag', { method: 'POST', body: { email, gamerTag } });

// Google sign-in: the backend verifies the Google credential, creates or
// finds the account, and returns a cross-app session token the wheel can
// use directly (bitWheelAuth validates the same token).
export const googleAuth = (credential, mode) =>
  request('/api/google-auth', {
    method: 'POST',
    body: { token: credential, mode, signupSource: 'bit-wheel' }
  });
